import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'

/**
 * Mehr Platz für den Titel, als er neben den Buttons braucht, ist nie nötig — reicht
 * er aber nicht, bleiben so viele Pixel für seine erste Zeile übrig; der Rest fließt
 * unter den Buttons weiter. So breit wie Icon und Griff (64 + 6 + 20): Mit und ohne
 * Icon brauchen die Buttons damit dieselbe Spalte — die Mindestbreite der Liste neben
 * dem Canvas (`LIST_MIN_WIDTH`, #296). Bis dahin 240px; damit verschwanden die Buttons,
 * lange bevor die Liste ihr Minimum erreichte.
 */
export const TITLE_MIN_ROOM = 90

interface HeaderActionsFitInput {
  /** Inhaltsbreite der Kopfzeile (ohne ihre Polsterung). */
  rowWidth: number
  /** Breite der Knopfreihe, zuletzt gemessen, als sie sichtbar war. */
  actionsWidth: number
  /** Linker Innenabstand des Titelfelds. */
  titleInsetLeft: number
  /** Mindestabstand zwischen Titeltext und Knöpfen. */
  gap: number
  /** Breite des Titels in einer Zeile. */
  titleTextWidth: number
  /** Breite der Icon-Zeile samt Einzug — 0 ohne Icon. */
  iconRowWidth?: number
}

/**
 * Ob die Knöpfe weichen müssen: dann, wenn der Titel neben ihnen nicht mehr in eine
 * Zeile passt (höchstens `TITLE_MIN_ROOM` gefordert). Mit Icon steht der Titel darunter
 * (#296) — dann zählt nur, ob die Knöpfe neben die Icon-Zeile passen.
 * Die Rechnung hängt nicht davon ab, ob die Knöpfe gerade zu sehen sind — sonst
 * schaltete das Ausblenden sich selbst wieder ab.
 */
export function shouldHideHeaderActions({ rowWidth, actionsWidth, titleInsetLeft, gap, titleTextWidth, iconRowWidth = 0 }: HeaderActionsFitInput): boolean {
  if (rowWidth <= 0 || actionsWidth <= 0) return false
  if (iconRowWidth > 0) return rowWidth - actionsWidth - gap < iconRowWidth
  const room = rowWidth - actionsWidth - titleInsetLeft - gap
  return room < Math.min(Math.ceil(titleTextWidth), TITLE_MIN_ROOM)
}

/**
 * Wie die Knöpfe neben dem Titel stehen: mit Beschriftung, nur als Zeichen oder gar nicht (Sven, 05.10.2026:
 * beim Verkleinern erst die Beschriftungen weg, dann erst die Knöpfe).
 */
export type HeaderActionsMode = 'full' | 'compact' | 'hidden'

interface HeaderActionsModeInput extends Omit<HeaderActionsFitInput, 'actionsWidth'> {
  /** Breite der Knopfreihe mit Beschriftungen, zuletzt gemessen. */
  labeledWidth: number
  /** Breite nur mit Zeichen — 0, solange sie so noch nie zu sehen war. */
  compactWidth: number
}

/**
 * Die Stufe für diese Breite: mit Beschriftung, solange der Titel daneben passt; sonst ohne; sonst gar nicht.
 * Beide Breiten sind die zuletzt gemessenen — die Wahl hängt nicht davon ab, welche gerade zu sehen ist.
 */
export function headerActionsMode({ labeledWidth, compactWidth, ...rest }: HeaderActionsModeInput): HeaderActionsMode {
  if (!shouldHideHeaderActions({ ...rest, actionsWidth: labeledWidth })) return 'full'
  // Ohne Beschriftung noch nie gemessen: erst so zeigen — der Beobachter misst und rechnet dann neu.
  if (compactWidth <= 0) return 'compact'
  return shouldHideHeaderActions({ ...rest, actionsWidth: compactWidth }) ? 'hidden' : 'compact'
}

let measureContext: CanvasRenderingContext2D | null | undefined

function measureText(text: string, font: string): number | null {
  if (measureContext === undefined) {
    try {
      measureContext = document.createElement('canvas').getContext('2d')
    } catch {
      measureContext = null
    }
  }
  if (!measureContext) return null
  measureContext.font = font
  return measureContext.measureText(text).width
}

/**
 * **Knöpfe neben dem Projekttitel weichen erst, wenn der Titel nicht mehr passt** — zuerst ihre
 * Beschriftungen, dann sie selbst ({@link headerActionsMode}).
 *
 * Offene Navigation und geteilter Canvas lassen der Liste gut 300px. Eine feste
 * Schwelle blendete die Knöpfe zu früh aus — auch dann, wenn ein kurzer Titel
 * noch bequem daneben stand. Gemessen wird der Titel selbst (bzw. der Platzhalter),
 * in der Schrift seines Feldes.
 *
 * Kopfzeile, Knopfreihe und Icon-Zeile hängen sich spät ein (Lade-Hülle, leerer Zustand,
 * Icon gesetzt) — deshalb Callback-Refs, die beim Einhängen beobachten.
 *
 * Schreibt außerdem `--header-actions-room` an die Kopfzeile: Knopfbreite samt Abstand. So viel
 * hält die erste Titelzeile ohne Icon rechts frei (`ProjectPage.scss`); ohne Knöpfe nichts.
 *
 * @example
 * const { rowRef, actionsRef, iconRowRef, actionsMode } = useHeaderActionsFit(titleRef, name || placeholder)
 * <div ref={rowRef} className={actionsMode === 'hidden' ? 'row row--actions-hidden' : 'row'}>
 */
export function useHeaderActionsFit(titleRef: RefObject<HTMLElement | null>, titleText: string) {
  const [actionsMode, setActionsMode] = useState<HeaderActionsMode>('full')
  // Welche Stufe im DOM steht — gemessen wird die Breite DIESER Stufe.
  const modeRef = useRef<HeaderActionsMode>('full')
  useLayoutEffect(() => { modeRef.current = actionsMode }, [actionsMode])
  const rowElRef = useRef<HTMLElement | null>(null)
  const actionsElRef = useRef<HTMLElement | null>(null)
  const iconRowElRef = useRef<HTMLElement | null>(null)
  const labeledWidthRef = useRef(0)
  const compactWidthRef = useRef(0)
  const textRef = useRef(titleText)
  textRef.current = titleText
  const observerRef = useRef<ResizeObserver | null>(null)

  const measure = useCallback(() => {
    const row = rowElRef.current
    const title = titleRef.current
    if (!row || !title) return
    // Verborgen (inaktiver Tab, Listenmodus): nichts messen, nichts umschalten.
    if (row.clientWidth === 0) return

    const actions = actionsElRef.current
    if (actions && actions.offsetWidth > 0) {
      if (modeRef.current === 'full') labeledWidthRef.current = actions.offsetWidth
      if (modeRef.current === 'compact') compactWidthRef.current = actions.offsetWidth
    }
    if (!actions) {
      row.style.removeProperty('--header-actions-room')
      setActionsMode('full')
      return
    }

    const rowStyle = getComputedStyle(row)
    const gap = parseFloat(rowStyle.getPropertyValue('--title-actions-gap')) || 0
    if (actions.offsetWidth > 0) row.style.setProperty('--header-actions-room', `${actions.offsetWidth + gap}px`)
    const titleStyle = getComputedStyle(title)
    // Nicht `titleStyle.font`: Der Shorthand kommt leer zurück, sobald eine
    // Teil-Eigenschaft nicht hineinpasst — die Canvas maß dann in 10px sans-serif.
    const font = `${titleStyle.fontStyle} ${titleStyle.fontWeight} ${titleStyle.fontSize} ${titleStyle.fontFamily}`
    const textWidth = measureText(textRef.current, font)
    if (textWidth === null) return

    setActionsMode(headerActionsMode({
      rowWidth: row.clientWidth - parseFloat(rowStyle.paddingLeft) - parseFloat(rowStyle.paddingRight),
      labeledWidth: labeledWidthRef.current,
      compactWidth: compactWidthRef.current,
      titleInsetLeft: parseFloat(titleStyle.paddingLeft),
      gap,
      titleTextWidth: textWidth,
      iconRowWidth: iconRowElRef.current?.offsetWidth ?? 0,
    }))
  }, [titleRef])

  const observe = useCallback((node: HTMLElement) => {
    observerRef.current ??= new ResizeObserver(() => measure())
    observerRef.current.observe(node)
  }, [measure])

  const rowRef = useCallback((node: HTMLDivElement | null) => {
    rowElRef.current = node
    if (!node) return
    observe(node)
    measure()
    return () => {
      observerRef.current?.unobserve(node)
      if (rowElRef.current === node) rowElRef.current = null
    }
  }, [measure, observe])

  const actionsRef = useCallback((node: HTMLDivElement | null) => {
    actionsElRef.current = node
    if (!node) return
    observe(node)
    measure()
    return () => {
      observerRef.current?.unobserve(node)
      if (actionsElRef.current === node) actionsElRef.current = null
      // Ohne Knöpfe (leeres Projekt) darf die Titelzeile ihnen keinen Platz mehr freihalten.
      measure()
    }
  }, [measure, observe])

  const iconRowRef = useCallback((node: HTMLDivElement | null) => {
    iconRowElRef.current = node
    if (!node) return
    observe(node)
    measure()
    return () => {
      observerRef.current?.unobserve(node)
      if (iconRowElRef.current === node) iconRowElRef.current = null
      measure()
    }
  }, [measure, observe])

  // Kein `disconnect` beim Aushängen: Die Aufräumer der Callback-Refs lösen die
  // Knoten, ein Beobachter ohne Ziele bleibt stumm.
  useEffect(() => {
    measure()
  }, [titleText, measure])

  return { rowRef, actionsRef, iconRowRef, actionsMode }
}
